import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth";
import { api, ApiError } from "@/lib/api";
import { analytics } from "@/lib/analytics";
import { CopyButton } from "@/components/CopyButton";
import { useCurrentPlan } from "@/hooks/useCurrentPlan";
import type { Domain } from "@dmarc/shared";

// DNS Provider badge with help links
function DnsProviderBadge({ domainId, showLinks = true }: { domainId: string; showLinks?: boolean }) {
  const { data, isLoading } = useQuery({
    queryKey: ["dns-provider", domainId],
    queryFn: () => api.domains.dnsProvider(domainId),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });

  if (isLoading) {
    return (
      <span className="text-xs text-muted-foreground">Detecting DNS provider...</span>
    );
  }

  if (!data || data.provider.id === "unknown") {
    return null;
  }

  const { provider } = data;
  const hasLinks = provider.links && Object.keys(provider.links).length > 0;

  return (
    <div className="mt-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
      <div className="flex items-center gap-2">
        <svg className="h-4 w-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7m0 0a3 3 0 01-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z" />
        </svg>
        <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
          DNS managed by {provider.name}
        </span>
      </div>
      {showLinks && hasLinks && (
        <div className="mt-2 flex flex-wrap gap-2">
          {provider.links.addTxtRecord && (
            <a
              href={provider.links.addTxtRecord}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
              Add TXT Record
            </a>
          )}
          {provider.links.spf && (
            <a
              href={provider.links.spf}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
              SPF Guide
            </a>
          )}
          {provider.links.dkim && (
            <a
              href={provider.links.dkim}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
              DKIM Guide
            </a>
          )}
          {provider.links.dmarc && (
            <a
              href={provider.links.dmarc}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
              DMARC Guide
            </a>
          )}
          {provider.links.securityHeaders && (
            <a
              href={provider.links.securityHeaders}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
              Security Headers
            </a>
          )}
        </div>
      )}
    </div>
  );
}


export function DomainsPage() {
  const { currentOrganization } = useAuthStore();
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [addError, setAddError] = useState("");
  const [verifyResult, setVerifyResult] = useState<{ domainId: string; success: boolean; message: string } | null>(null);

  // Plan limits
  const { plan, domainLimit } = useCurrentPlan();

  const { data: domains, isLoading } = useQuery<Domain[]>({
    queryKey: ["domains", currentOrganization?.id],
    queryFn: () => api.domains.list(currentOrganization!.id),
    enabled: !!currentOrganization,
  });

  const addMutation = useMutation({
    mutationFn: (domain: string) =>
      api.domains.create({ domain, organizationId: currentOrganization!.id }),
    onSuccess: () => {
      analytics.domainAdded();
      queryClient.invalidateQueries({ queryKey: ["domains"] });
      setShowAddModal(false);
      setNewDomain("");
      setAddError("");
    },
    onError: (err: ApiError) => {
      setAddError(err.message);
    },
  });

  const verifyMutation = useMutation({
    mutationFn: (id: string) => api.domains.verify(id),
    onSuccess: (data, domainId) => {
      if (data.verified) {
        analytics.domainVerified();
        setVerifyResult({ domainId, success: true, message: "Domain verified successfully!" });
      } else {
        setVerifyResult({
          domainId,
          success: false,
          message: data.message || "TXT record not found. DNS changes can take up to 48 hours to propagate."
        });
      }
      queryClient.invalidateQueries({ queryKey: ["domains"] });
    },
    onError: (err: ApiError, domainId) => {
      setVerifyResult({
        domainId,
        success: false,
        message: err.message || "Verification failed. Please try again."
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.domains.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["domains"] });
    },
  });

  const checkDnsMutation = useMutation({
    mutationFn: (id: string) => api.domains.dnsStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["domains"] });
    },
  });

  const updatePolicyMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: string; configuredPolicy?: string; configuredPct?: number }) =>
      api.domains.updatePolicy(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["domains"] });
    },
  });

  // Generate DMARC record based on policy settings
  const generateDmarcRecord = (domain: Domain) => {
    const policy = domain.configuredPolicy || "none";
    const pct = domain.configuredPct ?? 100;
    const sp = domain.configuredSp;

    let record = `v=DMARC1; p=${policy}; rua=mailto:${domain.domain}@rua.getsent.dev`;
    if (sp) record += `; sp=${sp}`;
    if (pct < 100) record += `; pct=${pct}`;
    return record;
  };

  if (!currentOrganization) {
    return <div>No organization selected</div>;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="page-header page-header-teal">
        <div className="absolute inset-0 bg-grid-white/10" />
        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="page-header-content">
            <div className="page-header-icon hidden sm:block">
              <svg className="icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold">Domains</h1>
              <p className="text-white/80 text-sm sm:text-base">Manage domains for {currentOrganization.name}</p>
            </div>
          </div>
          <button
            onClick={() => {
              if (domains && domains.length >= domainLimit) {
                setShowUpgradeModal(true);
              } else {
                setShowAddModal(true);
              }
            }}
            className="btn btn-glass w-full sm:w-auto"
          >
            Add Domain
            {domainLimit !== Infinity && domains && (
              <span className="ml-2 text-xs opacity-75">
                ({domains.length}/{domainLimit})
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Domain list */}
      {isLoading ? (
        <div className="loading-spinner">
          <div className="animate-pulse text-muted-foreground">Loading domains...</div>
        </div>
      ) : domains?.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon card-icon-teal">
            <svg className="icon-xl text-teal" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-semibold">No domains yet</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Add your first domain to start monitoring DMARC reports
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-teal btn-lg mt-6"
          >
            Add Your First Domain
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {domains?.map((domain) => (
            <div
              key={domain.id}
              className="card card-hover relative"
            >
              <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <div className="card-icon card-icon-teal hidden sm:flex">
                      <svg className="icon-md text-teal" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                      </svg>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-base sm:text-lg font-semibold break-all">{domain.domain}</span>
                      {domain.verified ? (
                        <span className="badge badge-success">
                          <svg className="icon-xs" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          Verified
                        </span>
                      ) : (
                        <span className="badge badge-warning">
                          <svg className="icon-xs" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Pending
                        </span>
                      )}
                    </div>
                  </div>
                  {!domain.verified && (
                    <div className="muted-panel mt-4">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <svg className="icon-md text-warning" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                        </svg>
                        Verification required
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground">
                        Add this TXT record to your DNS to verify domain ownership:
                      </p>
                      <div className="mt-3 space-y-2 text-sm">
                        <div className="info-row">
                          <span className="info-label">Name:</span>
                          <code className="info-value">_dmarc-verify</code>
                          <CopyButton text="_dmarc-verify" />
                        </div>
                        <div className="info-row">
                          <span className="info-label">Value:</span>
                          <code className="info-value truncate">{domain.verificationToken}</code>
                          <CopyButton text={domain.verificationToken} />
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground">
                          Your DNS provider will automatically append .{domain.domain}
                        </p>
                      </div>
                      <div className="mt-4 flex items-center gap-3">
                        <button
                          onClick={() => {
                            setVerifyResult(null);
                            verifyMutation.mutate(domain.id);
                          }}
                          disabled={verifyMutation.isPending}
                          className="btn btn-teal btn-sm"
                        >
                          {verifyMutation.isPending ? "Checking..." : "Verify Now"}
                        </button>
                        <span className="text-xs text-muted-foreground">
                          DNS propagation can take a few minutes to 48 hours
                        </span>
                      </div>
                      {verifyResult && verifyResult.domainId === domain.id && (
                        <div className={`mt-3 rounded-lg px-3 py-2 text-sm ${
                          verifyResult.success
                            ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300"
                            : "bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300"
                        }`}>
                          {verifyResult.message}
                        </div>
                      )}
                      <DnsProviderBadge domainId={domain.id} />
                    </div>
                  )}
                  {domain.verified && (
                    <div className="mt-4 space-y-4">
                      <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm">
                        {domain.dmarcRecord ? (
                          <span className="flex items-center gap-1.5 text-success">
                            <svg className="icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="hidden xs:inline">DMARC configured</span>
                            <span className="xs:hidden">DMARC</span>
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 text-warning">
                            <svg className="icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                            </svg>
                            <span className="hidden xs:inline">No DMARC record</span>
                            <span className="xs:hidden">No DMARC</span>
                          </span>
                        )}
                        {domain.spfRecord ? (
                          <span className="flex items-center gap-1.5 text-success">
                            <svg className="icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="hidden xs:inline">SPF configured</span>
                            <span className="xs:hidden">SPF</span>
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 text-warning">
                            <svg className="icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                            </svg>
                            <span className="hidden xs:inline">No SPF record</span>
                            <span className="xs:hidden">No SPF</span>
                          </span>
                        )}
                      </div>

                      {!domain.dmarcRecord ? (
                        <div className="muted-panel">
                          <div className="flex items-center gap-2 text-sm font-medium">
                            <svg className="icon-md text-teal" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Set up DMARC to receive reports
                          </div>

                          {/* Policy selector */}
                          <div className="mt-4 p-4 rounded-lg bg-background border">
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-sm font-medium">DMARC Policy</label>
                              <span className="text-xs text-muted-foreground">
                                Security: {(domain.configuredPolicy || "none") === "none" ? "Low" : domain.configuredPolicy === "quarantine" ? "Medium" : "High"}
                              </span>
                            </div>
                            {/* Security level indicator */}
                            <div className="flex gap-1 mb-4">
                              <div className={`h-1.5 flex-1 rounded-full ${(domain.configuredPolicy || "none") !== "none" ? "bg-amber-500" : "bg-amber-500"}`} />
                              <div className={`h-1.5 flex-1 rounded-full ${domain.configuredPolicy === "quarantine" || domain.configuredPolicy === "reject" ? "bg-orange-500" : "bg-muted"}`} />
                              <div className={`h-1.5 flex-1 rounded-full ${domain.configuredPolicy === "reject" ? "bg-emerald-500" : "bg-muted"}`} />
                            </div>
                            <div className="tabs">
                              <button
                                onClick={() => updatePolicyMutation.mutate({ id: domain.id, configuredPolicy: "none" })}
                                className={`tab flex-1 justify-center ${
                                  (domain.configuredPolicy || "none") === "none" ? "tab-active-amber" : "tab-inactive"
                                }`}
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                Monitor
                              </button>
                              <button
                                onClick={() => updatePolicyMutation.mutate({ id: domain.id, configuredPolicy: "quarantine" })}
                                className={`tab flex-1 justify-center ${
                                  domain.configuredPolicy === "quarantine" ? "tab-active-orange" : "tab-inactive"
                                }`}
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                </svg>
                                Quarantine
                              </button>
                              <button
                                onClick={() => updatePolicyMutation.mutate({ id: domain.id, configuredPolicy: "reject" })}
                                className={`tab flex-1 justify-center ${
                                  domain.configuredPolicy === "reject" ? "tab-active-green" : "tab-inactive"
                                }`}
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                                </svg>
                                Reject
                              </button>
                            </div>
                            {/* Policy explanation */}
                            <div className="mt-4 p-3 rounded-lg bg-muted/50 text-sm">
                              {(domain.configuredPolicy || "none") === "none" && (
                                <div className="space-y-2">
                                  <p className="font-medium text-amber-700 dark:text-amber-400">Monitor Mode (p=none)</p>
                                  <p className="text-muted-foreground">Emails are delivered normally, but you receive reports about authentication failures. <strong>Start here</strong> to understand your email sources before enforcing.</p>
                                </div>
                              )}
                              {domain.configuredPolicy === "quarantine" && (
                                <div className="space-y-2">
                                  <p className="font-medium text-orange-700 dark:text-orange-400">Quarantine Mode (p=quarantine)</p>
                                  <p className="text-muted-foreground">Emails failing authentication are sent to spam/junk folders. Good middle ground once you've verified legitimate senders.</p>
                                </div>
                              )}
                              {domain.configuredPolicy === "reject" && (
                                <div className="space-y-2">
                                  <p className="font-medium text-emerald-700 dark:text-emerald-400">Reject Mode (p=reject)</p>
                                  <p className="text-muted-foreground">Emails failing authentication are blocked entirely. <strong>Maximum protection</strong> against spoofing and phishing.</p>
                                </div>
                              )}
                            </div>
                            <p className="mt-3 text-xs text-muted-foreground">
                              Recommended path: Monitor → Quarantine → Reject. Review reports at each stage before increasing enforcement.
                            </p>
                          </div>

                          <p className="mt-4 text-sm text-muted-foreground">
                            Add this TXT record to your DNS:
                          </p>
                          <div className="mt-3 space-y-2 text-sm">
                            <div className="info-row">
                              <span className="info-label">Name:</span>
                              <code className="info-value">_dmarc</code>
                              <CopyButton text="_dmarc" />
                            </div>
                            <div className="info-row">
                              <span className="info-label">Value:</span>
                              <code className="info-value">
                                {generateDmarcRecord(domain)}
                              </code>
                              <CopyButton text={generateDmarcRecord(domain)} />
                            </div>
                          </div>
                          <button
                            onClick={() => checkDnsMutation.mutate(domain.id)}
                            disabled={checkDnsMutation.isPending}
                            className="btn btn-teal btn-sm mt-4"
                          >
                            {checkDnsMutation.isPending ? "Checking..." : "I've added the record - Check DNS"}
                          </button>
                          <DnsProviderBadge domainId={domain.id} />
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="success-panel">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="status-badge status-badge-success rounded-full">
                                  <svg className="icon-md text-success" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                                <div>
                                  <p className="font-medium text-green-800 dark:text-green-200">DMARC is configured!</p>
                                  {domain.reportCount && domain.reportCount > 0 ? (
                                    <p className="text-xs text-green-700 dark:text-green-300">
                                      {domain.reportCount} report{domain.reportCount !== 1 ? 's' : ''} received
                                      {domain.lastReportAt && (
                                        <> · Last: {new Date(domain.lastReportAt).toLocaleDateString()}</>
                                      )}
                                    </p>
                                  ) : (
                                    <p className="text-xs text-green-700 dark:text-green-300">
                                      Reports will start arriving within 24-48 hours
                                    </p>
                                  )}
                                </div>
                              </div>
                              {domain.reportCount && domain.reportCount > 0 && (
                                <Link
                                  to="/reports"
                                  search={{ domain: domain.id }}
                                  className="text-sm font-medium text-green-700 hover:text-green-600 dark:text-green-300"
                                >
                                  View Reports →
                                </Link>
                              )}
                            </div>
                          </div>

                          {/* Policy adjustment */}
                          <div className="muted-panel">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium">Policy Level</span>
                              <span className="text-xs text-muted-foreground">
                                Security: {(domain.configuredPolicy || "none") === "none" ? "Low" : domain.configuredPolicy === "quarantine" ? "Medium" : "High"}
                              </span>
                            </div>
                            {/* Security level indicator */}
                            <div className="flex gap-1 mb-4">
                              <div className={`h-1.5 flex-1 rounded-full ${(domain.configuredPolicy || "none") !== "none" ? "bg-amber-500" : "bg-amber-500"}`} />
                              <div className={`h-1.5 flex-1 rounded-full ${domain.configuredPolicy === "quarantine" || domain.configuredPolicy === "reject" ? "bg-orange-500" : "bg-muted"}`} />
                              <div className={`h-1.5 flex-1 rounded-full ${domain.configuredPolicy === "reject" ? "bg-emerald-500" : "bg-muted"}`} />
                            </div>
                            <div className="tabs">
                              <button
                                onClick={() => updatePolicyMutation.mutate({ id: domain.id, configuredPolicy: "none" })}
                                disabled={updatePolicyMutation.isPending}
                                className={`tab flex-1 justify-center ${
                                  (domain.configuredPolicy || "none") === "none" ? "tab-active-amber" : "tab-inactive"
                                }`}
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                Monitor
                              </button>
                              <button
                                onClick={() => updatePolicyMutation.mutate({ id: domain.id, configuredPolicy: "quarantine" })}
                                disabled={updatePolicyMutation.isPending}
                                className={`tab flex-1 justify-center ${
                                  domain.configuredPolicy === "quarantine" ? "tab-active-orange" : "tab-inactive"
                                }`}
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                </svg>
                                Quarantine
                              </button>
                              <button
                                onClick={() => updatePolicyMutation.mutate({ id: domain.id, configuredPolicy: "reject" })}
                                disabled={updatePolicyMutation.isPending}
                                className={`tab flex-1 justify-center ${
                                  domain.configuredPolicy === "reject" ? "tab-active-green" : "tab-inactive"
                                }`}
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                                </svg>
                                Reject
                              </button>
                            </div>
                            {/* Policy explanation */}
                            <div className="mt-4 p-3 rounded-lg bg-background text-sm">
                              {(domain.configuredPolicy || "none") === "none" && (
                                <div className="space-y-2">
                                  <p className="font-medium text-amber-700 dark:text-amber-400">Monitor Mode (p=none)</p>
                                  <p className="text-muted-foreground">Emails are delivered normally. You receive reports to identify legitimate senders before enforcing.</p>
                                </div>
                              )}
                              {domain.configuredPolicy === "quarantine" && (
                                <div className="space-y-2">
                                  <p className="font-medium text-orange-700 dark:text-orange-400">Quarantine Mode (p=quarantine)</p>
                                  <p className="text-muted-foreground">Failing emails go to spam/junk. Good balance of protection while allowing review.</p>
                                </div>
                              )}
                              {domain.configuredPolicy === "reject" && (
                                <div className="space-y-2">
                                  <p className="font-medium text-emerald-700 dark:text-emerald-400">Reject Mode (p=reject)</p>
                                  <p className="text-muted-foreground">Failing emails are blocked. Maximum protection against spoofing and phishing.</p>
                                </div>
                              )}
                            </div>

                            {/* Show DNS update instructions when policy differs from current record */}
                            {domain.dmarcRecord && !domain.dmarcRecord.includes(`p=${domain.configuredPolicy || "none"}`) && (
                              <div className="mt-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                                <div className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-200">
                                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                  </svg>
                                  Update your DNS record
                                </div>
                                <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
                                  To apply this policy change, update your <code className="px-1 py-0.5 bg-amber-100 dark:bg-amber-900/50 rounded">_dmarc</code> TXT record:
                                </p>
                                <div className="mt-3 p-2 rounded bg-white dark:bg-gray-900 border border-amber-200 dark:border-amber-800">
                                  <div className="flex items-center justify-between gap-2">
                                    <code className="text-xs break-all text-gray-800 dark:text-gray-200">
                                      {generateDmarcRecord(domain)}
                                    </code>
                                    <CopyButton text={generateDmarcRecord(domain)} />
                                  </div>
                                </div>
                                <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                                  Go to your DNS provider, find the _dmarc TXT record, and replace its value with the text above.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    if (confirm("Are you sure you want to delete this domain?")) {
                      deleteMutation.mutate(domain.id);
                    }
                  }}
                  className="absolute top-4 right-4 sm:relative sm:top-0 sm:right-0 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                  aria-label={`Delete domain ${domain.domain}`}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add domain modal */}
      {showAddModal && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="add-domain-title">
          <div className="modal">
            <div className="modal-header">
              <div className="card-icon card-icon-teal-solid">
                <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
              </div>
              <h2 id="add-domain-title" className="text-lg font-semibold">Add Domain</h2>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                addMutation.mutate(newDomain);
              }}
              className="space-y-4"
            >
              {addError && (
                <div className="alert alert-error">
                  <svg className="icon-md shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                  </svg>
                  <div>
                    {addError}
                    {addError.toLowerCase().includes("domain limit") && (
                      <Link to="/settings" className="ml-2 font-medium underline hover:no-underline">
                        Upgrade now
                      </Link>
                    )}
                  </div>
                </div>
              )}
              <div>
                <label htmlFor="new-domain-input" className="block text-sm font-medium mb-2">Domain</label>
                <input
                  id="new-domain-input"
                  type="text"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="example.com"
                  className="input input-teal"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewDomain("");
                    setAddError("");
                  }}
                  className="btn btn-ghost"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addMutation.isPending}
                  className="btn btn-teal"
                >
                  {addMutation.isPending ? "Adding..." : "Add Domain"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upgrade modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30">
                <svg className="w-6 h-6 text-teal-600 dark:text-teal-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold">Domain Limit Reached</h3>
            </div>
            <p className="text-muted-foreground mb-4">
              You've reached the {domainLimit} domain limit on the {plan} plan. Upgrade to monitor more domains:
            </p>
            <ul className="space-y-2 mb-6">
              {plan === "free" && (
                <li className="flex items-center gap-2 text-sm">
                  <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                  <span><strong>Starter:</strong> Up to 5 domains</span>
                </li>
              )}
              {(plan === "free" || plan === "starter") && (
                <li className="flex items-center gap-2 text-sm">
                  <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                  <span><strong>Pro:</strong> Up to 25 domains</span>
                </li>
              )}
              <li className="flex items-center gap-2 text-sm">
                <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                <span><strong>Business:</strong> Unlimited domains</span>
              </li>
            </ul>
            <div className="flex gap-3">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="btn btn-secondary flex-1"
              >
                Maybe Later
              </button>
              <Link
                to="/pricing"
                className="btn btn-primary flex-1 text-center"
                onClick={() => setShowUpgradeModal(false)}
              >
                View Plans
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
